/**
 * 移动端左侧悬浮菜单拉手
 * 方案 K：左边缘竖向"标签拉手"
 *
 * 设计目标：
 *  - 永远贴在屏幕左边缘中部，不占内容区空间
 *  - 周期性轻微向右弹动，强烈引导用户发现
 *  - 抽屉打开时图标变 ✕，方便关闭
 *
 * 使用说明：仅用于移动端，桌面端不渲染
 */

import React from 'react';
import { Box, useTheme } from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';

// 招手动画：每 3 秒向右轻微弹一下
const wiggleRight = keyframes`
  0%, 90%, 100% {
    transform: translate(0, -50%);
    box-shadow: 4px 0 16px rgba(0, 0, 0, 0.25);
  }
  93% {
    transform: translate(10px, -50%);
    box-shadow: 8px 0 24px rgba(0, 0, 0, 0.35);
  }
  96% {
    transform: translate(4px, -50%);
    box-shadow: 6px 0 20px rgba(0, 0, 0, 0.3);
  }
`;

// 光晕呼吸（只在打开前生效，吸引视线）
const glowPulse = keyframes`
  0%, 100% {
    filter: drop-shadow(0 0 0 rgba(255, 255, 255, 0));
  }
  50% {
    filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.6));
  }
`;

const MobileMenuHandle = ({ open, onClick }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const primaryDark = theme.palette.primary.dark;

  return (
    <Box
      onClick={onClick}
      role="button"
      aria-label={open ? '关闭个人菜单' : '打开个人菜单'}
      sx={{
        position: 'fixed',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: theme.zIndex.drawer + 2, // 比 Drawer 高，关闭时仍可见
        width: 36,
        height: 120,
        // 右半圆 + 左直角，贴边形状
        borderTopRightRadius: 24,
        borderBottomRightRadius: 24,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        background: `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`,
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        cursor: 'pointer',
        userSelect: 'none',
        boxShadow: '4px 0 16px rgba(0, 0, 0, 0.25)',
        // 关闭时持续招手；打开时停止抖动只保留正常状态
        animation: open
          ? 'none'
          : `${wiggleRight} 3s ease-in-out infinite`,
        transition: 'background 0.25s ease, width 0.2s ease',
        '&:hover': {
          background: `linear-gradient(135deg, ${primaryDark} 0%, ${primary} 100%)`,
          width: 42,
        },
        '&:active': {
          opacity: 0.85,
        },
      }}
    >
      {/* 图标：关闭态显示 ✕，否则显示菜单 + 呼吸光晕 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: open ? 'none' : `${glowPulse} 2s ease-in-out infinite`,
        }}
      >
        {open ? (
          <CloseIcon sx={{ fontSize: 22 }} />
        ) : (
          <MenuIcon sx={{ fontSize: 22 }} />
        )}
      </Box>

      {/* 竖向文字标签 */}
      <Box
        sx={{
          writingMode: 'vertical-rl',
          textOrientation: 'upright',
          fontSize: 12,
          letterSpacing: 2,
          fontWeight: 600,
          lineHeight: 1,
          mt: 0.5,
        }}
      >
        {open ? '关闭' : '菜单'}
      </Box>
    </Box>
  );
};

export default MobileMenuHandle;
